import { Card, Col, Empty, Row, Segmented, Spin } from 'antd'
import { useQuery } from '@apollo/client'
import 'chart.js/auto'
import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'
import React, { useState } from 'react'
import { AppointmentReportBucket, GetAppointmentReportQuery } from '../graphql/appointment.graphql'
import { translate } from '../../../core'

// Shared palette (mirrors the dashboard PieChart) so charts feel native to the app.
const COLORS = ['#567CDC', '#2349A9', '#A0B5EB', '#1B377E', '#040915', '#8FA3D8', '#3D5FBF', '#0B1B45']

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']


const formatBucket = (bucket: AppointmentReportBucket, unit: 'week' | 'month'): string => {
  const [year, month, day] = bucket.bucket.split('-').map(Number)
  const label =
    unit === 'month'
      ? `${MONTHS[month - 1]} ${year}`
      : `${MONTHS[month - 1]} ${day}`
  return bucket.isCurrent ? `${label} (${translate('current')})` : label
}

export const AppointmentReport = () => {
  const {data, loading} = useQuery(GetAppointmentReportQuery)
  const [unit, setUnit] = useState<'week' | 'month'>('month')

  const report = data?.appointmentReport
  const series = unit === 'week' ? report?.weekly ?? [] : report?.monthly ?? []
  const distribution = report?.distribution ?? []

  const barData: ChartData<'bar'> = {
    labels: series.map(it => formatBucket(it, unit)),
    datasets: [
      {
        label: translate('appointments'),
        data: series.map(it => it.count),
        backgroundColor: '#567CDC',
        borderRadius: 4,
      },
    ],
  }

  const barOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {legend: {display: false}},
    scales: {y: {beginAtZero: true, ticks: {precision: 0}}},
  }

  const doughnutData: ChartData<'doughnut'> = {
    labels: distribution.map(it => it.label ?? translate('service_type_unknown')),
    datasets: [
      {
        data: distribution.map(it => it.count),
        backgroundColor: distribution.map((_, i) => COLORS[i % COLORS.length]),
        borderColor: '#fff',
        borderWidth: 1,
      },
    ],
  }

  const doughnutOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {legend: {position: 'bottom'}},
  }

  return (
    <Row gutter={[16, 16]} style={{marginBottom: 16}}>
      <Col xs={24} lg={14}>
        <Card
          size='small'
          title={translate('appointment_volume')}
          extra={
            <Segmented
              size='small'
              value={unit}
              onChange={value => setUnit(value as 'week' | 'month')}
              options={[
                {label: translate('weekly'), value: 'week'},
                {label: translate('monthly'), value: 'month'},
              ]}
            />
          }
        >
          <div style={{height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            {loading ? (
              <Spin/>
            ) : series.length === 0 ? (
              <Empty description={translate('no_data_available')}/>
            ) : (
              <Chart type='bar' data={barData} options={barOptions}/>
            )}
          </div>
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        <Card size='small' title={translate('service_type_distribution')}>
          <div style={{height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            {loading ? (
              <Spin/>
            ) : distribution.length === 0 ? (
              <Empty description={translate('no_data_available')}/>
            ) : (
              <Chart type='doughnut' data={doughnutData} options={doughnutOptions}/>
            )}
          </div>
        </Card>
      </Col>
    </Row>
  )
}
